import React, { useMemo, useState } from "react";
import { CompanyResult, CreateCompanyForm } from "src/interfaces/company";
import {
  Box,
  Card,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

interface CreateCompanyProps {
  values: CreateCompanyForm;
  errors: any;
  handleChange: any;
  onPressCreate: () => void;
  result?: CompanyResult;
  isLoading: boolean;
}

const RestaurentTypes = [
  { value: "pz", label: "Pizza" },
  { value: "rs", label: "Restaurant" },
  { value: "cf", label: "Coffee" },
  { value: "pb", label: "Pub" },
  { value: "bky", label: "Bakery" },
  { value: "cky", label: "Chicken" },
  { value: "sh", label: "Sushi" },
];

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" } };

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize="11px" fontWeight={800} color="#4338CA" sx={{ textTransform: "uppercase", letterSpacing: "0.08em", mb: 2.5 }}>
    {children}
  </Typography>
);

const Panel = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ p: 3, height: "100%", borderRadius: "16px", border: "1px solid #EEF1F5", bgcolor: "#FAFBFC" }}>
    {children}
  </Box>
);

const CreateCompanyLayout = ({
  handleChange,
  values,
  errors,
  onPressCreate,
  result,
  isLoading,
}: CreateCompanyProps) => {
  const [copiedAdminUsername, setCopiedAdminUsername] = useState(false);
  const [copiedAdminPassword, setCopiedAdminPassword] = useState(false);

  // Tyfhu operates in 4 markets only — keep this list in sync with pricing regions.
  const countries = useMemo(
    () => [
      { label: "United Kingdom", value: "GB" },
      { label: "United States", value: "US" },
      { label: "Australia", value: "AU" },
      { label: "India", value: "IN" },
    ],
    []
  );

  const handleCopy = (text: string, setCopiedState: React.Dispatch<React.SetStateAction<boolean>>) => {
    navigator.clipboard.writeText(text);
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), 1500);
  };

  const handleSaveCSV = () => {
    if (!result) return;
    const headers = ["Role", "Username", "Password"];
    const rows = [
      ["Admin", result.admin, result.adminPassword],
      ...(result.stores || []).map((s) => ["Store Manager", s.username, s.password]),
    ];
    const csvString = [
      headers.join(","),
      ...rows.map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")),
    ].join("\r\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `credentials_${result.admin}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box display="flex" flexDirection="column" gap={3} sx={{ maxWidth: 950, width: "100%", mx: "auto" }}>
      {/* FORM CARD */}
      <Card variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: "20px", borderColor: "#EEF1F5", boxShadow: "0 1px 3px rgba(16,24,40,0.04)" }}>
        {/* Header */}
        <Box display="flex" gap={2} alignItems="center" mb={3.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: "#EEF2FF",
              color: "#4338CA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography fontSize="22px" fontWeight={800} sx={{ letterSpacing: "-0.5px", lineHeight: 1.2 }}>
              Create Company
            </Typography>
            <Typography fontSize="13px" color="text.secondary" sx={{ mt: 0.25 }}>
              Register a new company and generate admin credentials
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* LEFT PANEL */}
          <Grid item xs={12} md={6}>
            <Panel>
              <SectionLabel>Company Information</SectionLabel>
              <Box display="flex" flexDirection="column" gap={2.5}>
                <TextField fullWidth label="Company Name" placeholder="e.g. Pizza Hut" value={values.company_name} error={Boolean(errors?.company_name)} helperText={errors?.company_name} onChange={handleChange("company_name")} sx={inputSx} />
                <TextField fullWidth label="Company Slug" placeholder="e.g. pizzahut" value={values.company_slug} error={Boolean(errors?.company_slug)} helperText={errors?.company_slug || "Used to generate usernames (e.g. pizzahut-admin)"} onChange={handleChange("company_slug")} sx={inputSx} />
                <TextField fullWidth label="Contact Email" type="email" placeholder="e.g. contact@pizzahut.com" value={values.email} error={Boolean(errors?.email)} helperText={errors?.email} onChange={handleChange("email")} sx={inputSx} />
              </Box>
            </Panel>
          </Grid>

          {/* RIGHT PANEL */}
          <Grid item xs={12} md={6}>
            <Panel>
              <SectionLabel>Operational Details</SectionLabel>
              <Box display="flex" flexDirection="column" gap={2.5}>
                <TextField fullWidth label="Number of Branches" type="number" placeholder="e.g. 5" value={values.no_of_branches} error={Boolean(errors?.no_of_branches)} helperText={errors?.no_of_branches} onChange={handleChange("no_of_branches")} sx={inputSx} />
                <TextField select fullWidth label="Restaurant Type" value={values.restaurant_type} error={Boolean(errors?.restaurant_type)} helperText={errors?.restaurant_type} onChange={handleChange("restaurant_type")} sx={inputSx}>
                  {RestaurentTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
                <TextField select fullWidth label="Country" value={values.company_country} error={Boolean(errors?.company_country)} helperText={errors?.company_country} onChange={handleChange("company_country")} sx={inputSx}>
                  {countries.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </Panel>
          </Grid>
        </Grid>

        {/* CTA */}
        <Box mt={3.5} display="flex" justifyContent="flex-end">
          <LoadingButton
            variant="contained"
            loading={isLoading}
            onClick={onPressCreate}
            size="large"
            sx={{ px: 5, height: 46, borderRadius: "12px", fontWeight: 700, textTransform: "none", boxShadow: "0 6px 16px rgba(99,102,241,0.25)" }}
          >
            Create Company
          </LoadingButton>
        </Box>
      </Card>

      {/* SUCCESS CREDENTIALS DISPLAY */}
      {result && (
        <Card
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "20px",
            background: "linear-gradient(135deg, #0f172a, #1e293b)",
            color: "#f8fafc",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CheckCircleIcon sx={{ color: "#10b981", fontSize: 30 }} />
              <Box>
                <Typography fontSize="18px" fontWeight={800} color="#ffffff">Company Created Successfully</Typography>
                <Typography fontSize="13px" color="#94a3b8" sx={{ mt: 0.25 }}>Credentials generated. Save them securely.</Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleSaveCSV}
              sx={{ background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", borderRadius: "12px", px: 3, height: 40, fontWeight: 700, textTransform: "none", "&:hover": { background: "linear-gradient(135deg, #2563eb, #1d4ed8)" } }}
            >
              Save as CSV
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Admin Credentials */}
            <Grid item xs={12} md={5}>
              <Box sx={{ background: "rgba(255,255,255,0.03)", p: 3, borderRadius: "16px", border: "1px solid rgba(255,255,255,0.08)", height: "100%" }}>
                <Typography fontSize="11px" fontWeight={800} sx={{ color: "#38bdf8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 3 }}>
                  Admin Credentials
                </Typography>
                <Box mb={2.5}>
                  <Typography fontSize="11px" sx={{ color: "#94a3b8", mb: 0.5 }}>Username</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontSize="14px" fontWeight={700} color="#fff" sx={{ wordBreak: "break-all" }}>{result.admin}</Typography>
                    <Tooltip title={copiedAdminUsername ? "Copied!" : "Copy username"}>
                      <IconButton size="small" onClick={() => handleCopy(result.admin, setCopiedAdminUsername)} sx={{ color: "#94a3b8" }}>
                        {copiedAdminUsername ? <CheckIcon fontSize="small" sx={{ color: "#10b981" }} /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box>
                  <Typography fontSize="11px" sx={{ color: "#94a3b8", mb: 0.5 }}>Temporary Password</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontSize="14px" fontWeight={700} color="#fff" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{result.adminPassword}</Typography>
                    <Tooltip title={copiedAdminPassword ? "Copied!" : "Copy password"}>
                      <IconButton size="small" onClick={() => handleCopy(result.adminPassword, setCopiedAdminPassword)} sx={{ color: "#94a3b8" }}>
                        {copiedAdminPassword ? <CheckIcon fontSize="small" sx={{ color: "#10b981" }} /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography fontSize="11px" sx={{ color: "#94a3b8", mt: 2.5, fontStyle: "italic", borderTop: "1px dashed rgba(255,255,255,0.1)", pt: 2, lineHeight: 1.4 }}>
                  * Admin can log in using either this username or the registered contact email.
                </Typography>
              </Box>
            </Grid>

            {/* Store Manager Credentials */}
            <Grid item xs={12} md={7}>
              <Box sx={{ background: "rgba(0,0,0,0.2)", p: 3, borderRadius: "16px", border: "1px solid rgba(255,255,255,0.04)", height: "100%" }}>
                <Typography fontSize="11px" fontWeight={800} sx={{ color: "#38bdf8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 2 }}>
                  Store Manager Accounts ({(result.stores || []).length})
                </Typography>
                <Box sx={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.25, pr: 1, "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.12)", borderRadius: "3px" } }}>
                  {(result.stores || []).map((store, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" alignItems="center" sx={{ borderBottom: index < result.stores.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none", pb: index < result.stores.length - 1 ? 1.25 : 0 }}>
                      <Typography fontSize="13px" fontWeight={600} color="#e2e8f0">{store.username}</Typography>
                      <Typography fontSize="13px" fontFamily="monospace" color="#cbd5e1">{store.password}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      )}
    </Box>
  );
};

export default CreateCompanyLayout;
